import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

import ITask = ZoningAPI.ITask;
import Response = ZoningAPI.Response;

import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import * as _ from 'lodash';
import { BaseAPIService } from '../../../shared/services/base-api.service';
import { GeneralProcessType } from '../../scheduler/enums/general-process-type';
import IProcessesStatisticDTO = ZoningAPI.IProcessesStatisticDTO;
import { Task } from 'src/app/shared/models/task';
import { TaskService } from './task.service';
import { JobsService } from './jobs.service';
import { IProcessesStatistic } from 'src/app/shared/interfaces/processes-statistic.interface';
import { ProcessesStatisticMapper } from 'src/app/shared/mappers/processes-statistic.mapper';

const PROCESS_API_ENDPOINT = environment.ZONAR_API_URL + '/process';

@Injectable()
export class ProcessService extends BaseAPIService {
    statistic: IProcessesStatistic;
    mapper: ProcessesStatisticMapper;

    constructor(
        private readonly http: HttpClient,
        private readonly taskService: TaskService,
        private readonly jobsService: JobsService
    ) {
        super();
        this.mapper = new ProcessesStatisticMapper();
    }

    get processesCount(): number {
        return _.get(this.statistic, 'processesCount', 0);
    }

    get runningThreadsCount(): number {
        return _.get(this.statistic, 'runningThreadsCount', 0);
    }

    get maxThreadsCount(): number {
        return _.get(this.statistic, 'maxThreadsCount', 0);
    }

    get availableThreadsCount(): number {
        return _.get(this.statistic, 'availableThreadsCount', 0);
    }

    get availableToReserveThreadsCount(): number {
        return _.get(this.statistic, 'availableToReserveThreadsCount', 0);
    }

    get usedTreadsCapacity(): number {
        return this.maxThreadsCount ? _.round((this.runningThreadsCount * 100) / this.maxThreadsCount, 2) : 0;
    }

    get activeProcessesCount(): number {
        return _.get(this.statistic, 'activeProcessesCount', 0);
    }

    get allowedThreadsCount(): number {
        return _.get(this.statistic, 'allowedThreadsCount', 0);
    }

    get threadsUpdateTimeStr(): string {
        return _.get(this.statistic, 'threadsUpdateTimeStr');
    }

    get alreadyReservedThreadsCount(): number {
        return _.get(this.statistic, 'alreadyReservedThreadsCount', 0);
    }

    get threadsIncreaseSchedulerActive(): boolean {
        return _.get(this.statistic, 'threadsIncreaseSchedulerActive', false);
    }

    get availableThreadsCountValidated(): number {
        return this.threadsIncreaseSchedulerActive ? this.availableToReserveThreadsCount : this.availableThreadsCount;
    }

    get processesSchedulerActive(): boolean {
        return _.get(this.statistic, 'processesSchedulerActive', false);
    }

    stopAllProcesses(processType?: GeneralProcessType): Observable<ITask[]> {
        const params: any = {
            fromObject: {}
        };
        if (processType) {
            params.fromObject.processType = String(processType);
        }
        return this.http
            .get<Response>(`${PROCESS_API_ENDPOINT}/stop`, {
                params: new HttpParams(params)
            })
            .pipe(map(response => this.getData<ITask[]>(response, [])));
    }

    getProcessesStatistic(): Observable<IProcessesStatistic> {
        return this.http.get<Response>(`${PROCESS_API_ENDPOINT}/statistic`).pipe(
            map(response => {
                this.statistic = this.mapper.fromDTO(this.getData<IProcessesStatisticDTO>(response));
                return this.statistic;
            })
        );
    }

    setProcessesStatistic(statistic: IProcessesStatistic) {
        this.statistic = statistic;
    }

    updateProcesses(updatedProcessesData: ITask[]) {
        if (_.isEmpty(updatedProcessesData)) {
            return;
        }
        /**
         * Because processes works asynchronously they may produce events after they were destroyed.
         * Need to prevent update of destroyed tasks.
         */
        const processesToUpdate = updatedProcessesData.filter(process => !process.isDestroyed);

        this.taskService.addOrUpdateProcessesWithData(
            processesToUpdate.filter(data => Task.isTask(data)),
            !this.taskService.containsProcessesForInstance
        );
        this.jobsService.addOrUpdateProcessesWithData(processesToUpdate.filter(data => Task.isJob(data)));
        this.jobsService.triggerUpdates();
    }

    removeProcesses(stateEnv: string, ids: string[]): Observable<ITask[]> {
        if (_.isEmpty(ids)) {
            return new Observable();
        }
        return this.http
            .request<Response>('DELETE', `${PROCESS_API_ENDPOINT}`, {
                body: { ids },
                params: {
                    state_env: stateEnv
                }
            })
            .pipe(
                map(response => {
                    return this.getData<ITask[]>(response);
                }),
                map((processesData: ITask[]) => {
                    return processesData;
                })
            );
    }

    archiveAndRemoveProcesses(stateEnv: string, ids: string[], restore?: boolean): Observable<ITask[]> {
        return this.archiveProcesses(stateEnv, ids, restore).pipe(
            map((processesData: ITask[]) => {
                return processesData;
            })
        );
    }

    archiveProcesses(stateEnv: string, ids: string[], restore?: boolean): Observable<ITask[]> {
        if (_.isEmpty(ids)) {
            return new Observable<ITask[]>();
        }
        const params: any = {
            state_env: stateEnv
        };
        if (restore) {
            params.restore = restore;
        }
        return this.http.patch<Response>(`${PROCESS_API_ENDPOINT}/archive`, { ids }, { params }).pipe(
            map(response => {
                return this.getData<ITask[]>(response);
            })
        );
    }

    removeJobsAndTasks(processesData: ITask[]) {
        if (_.isEmpty(processesData)) {
            return processesData;
        }
        this.taskService.removeTasks(processesData);
        this.notifyAllJobsLists();
        return processesData;
    }

    processNewProcess(processData: ITask) {
        if (!processData) {
            return;
        }
        Task.isTask(processData)
            ? this.taskService.processNewProcess(processData)
            : this.jobsService.processNewProcess(processData);
    }

    notifyAllJobsLists() {
        this.jobsService.notifyProcessListChanged();
        this.jobsService.notifyProcessListChanged(true);
    }
}
